import {Module} from "vuex";
import {User} from "@/types";
import {
    getGroupsGeneric,
    getHistoricMessages,
    getSubjectsGeneric,
    getUsersGeneric,
    sendMessageService
} from "@/store/services/communication/messaging";
import { getEsignClientEventsApi, getOrderUserEmailsApi } from "@/store/services/task/contactMechanism";
import store from "@/store";
import { groupList } from "@/store/services/user/userService";
import { getAssociatedPartyInfo } from "@/store/services/order/orderService";

const normalize = (value: any): string => String(value ?? "").trim().toLowerCase();

/**
 * Emails des autres acteurs de la commande qui ont le même profil (rôle) que l'utilisateur connecté.
 * L'utilisateur est reconnu parmi les acteurs par son email, ou à défaut par son tiers (party).
 * Liste vide si la commande n'est pas chargée ou si l'utilisateur n'en est pas acteur.
 */
function sameProfileEmails(reference: string, userEmail: string): string[] {
    const orderModuleOrder = store.state.orderModule?.order;
    const order = orderModuleOrder?.reference === reference ? orderModuleOrder : store.state.taskModule?.order;
    if (!order || !Array.isArray(order.orderParties)) return [];

    const actors: any[] = getAssociatedPartyInfo(order, "order") || [];
    // email d'origine (le filtre de la requête est exact, casse comprise)
    const emailOf = (actor: any): string =>
        String(actor.contacts?.find((c: any) => c.label === "emailcontact")?.value ?? "").trim();
    const partyUser = store.state.authModule?.partyUser;
    const userThird = normalize(partyUser?.resourceUid?.id ?? partyUser?.resourceUid);
    const me = normalize(userEmail);

    const profiles = new Set(
        actors
            .filter((a) => normalize(emailOf(a)) === me || (userThird && normalize(a.thirdResourceUid) === userThird))
            .map((a) => a.associated)
    );
    const others = new Map<string, string>();
    for (const actor of actors) {
        const email = emailOf(actor);
        if (profiles.has(actor.associated) && email && normalize(email) !== me) {
            others.set(normalize(email), email);
        }
    }
    return Array.from(others.values());
}

const messagingModule: Module<any, any> = {
    namespaced: true,
    state: {
        message: {},
        subjects: [],
        groups: [],
        users: [],
        destinations: [],
        recipients: [],
        historicMessages: [],
        emailEvents: [],
        orderUserEmails: [],
        isSendingMessage: false
    },
    mutations: {
        /**
         * The setSubjects mutation function updates the subjects property in the state with the data contained in the payload argument.
         * @param state
         * @param payload
         */
        setSubjects: function (state, payload) {
            state.subjects = payload
        },
        /**
         * The setUsers mutation function updates the users property in the state with the data contained in the payload argument.
         * @param state
         * @param payload
         */
        setUsers: function (state, payload) {
            state.users = payload
        },

        /**
         * set groups
         * @param state
         * @param payload
         */
        setGroups: function (state, payload) {
            state.groups = payload
        },
        
        /**
         * set recipients
         * @param state
         * @param payload
         */
        setRecipients: function (state, payload) {
            state.recipients = payload
        },

        /**
         * set destinations
         * @param state
         * @param payload
         */
        setDestinations: function (state, payload) {
            state.destinations = payload
        },

        /**
         * set the current message to the state
         * @param state
         * @param payload
         */
        setMessage: function (state, payload) {
            state.message = payload
        },

        /**
         * set the sending status to the state
         * @param state
         * @param isSending
         */
        setIsSendingMessage(state, isSending) {
            state.isSendingMessage = isSending
        },
        /**
         * set the historic messages
         * @param state
         * @param payload
         */
        setHistoricMessages(state, payload) {
            state.historicMessages = payload;
        },
        /**
         * set the esign communication events (emailEvents)
         * @param state
         * @param payload
         */
        setEmailEvents(state, payload) {
            state.emailEvents = payload;
        },
        /**
         * set the emails where the connected user is sender or recipient (order-user-emails query)
         * @param state
         * @param payload
         */
        setOrderUserEmails(state, payload) {
            state.orderUserEmails = payload;
        }
    },
    actions: {
        /**
         * we call this asynchronous action to fetch subjects using getSubjectsGeneric
         * @param commit
         */
        async fetchSubjects({commit}) {
            try {
                const response = await getSubjectsGeneric();
                commit("setSubjects", response.items);
            } catch (error) {
                console.error(error);
            }
        },
        /**
         * we call this asynchronous action to fetch users using getUsersGeneric
         * @param commit
         */
        async fetchUsers({commit}) {
            try {
                const response = await getUsersGeneric()
                commit("setUsers", response.items);
            } catch (error) {

                return null;
            }

        },
        async fetchGroups({commit}) {
            try {
                const filterGroup =undefined
                const response = await groupList(filterGroup);
                if (response && response.length > 0) {
                    const updatedResponse = response.map((item: any) => ({
                        objectType: "odm.user.securitygroup",
                        systemUid: "odm-user",
                        resourceUid: item.groupid
                    }));
        
                    commit('setGroups', updatedResponse);
                } else {
                    commit('setGroups', []);
                }
            } catch (error) {

                return null;
            }
        },

        /**
         * commit optionsRecipients into state.recipients
         * @param commit
         * @param payload
         */
        async fetchRecipients({commit}) {
            store.dispatch("messagingModule/fetchUsers")
            store.dispatch("messagingModule/fetchGroups")

            // Merge the options of the list of Users and Groups
            const optionsGroupsList = store.getters["messagingModule/getGroups"]
            
            // Filter the optionsUsersList to only include users whose email is equal to anonymous users
            const filteredUsersList = store.getters["messagingModule/getUsers"].filter((user: User) => {
                if ( user.firstName !== 'anonymous' || user.lastName !== 'anonymous' ) {
                    return user
                }
            });
            // Merge the filtered optionsUsersList with the optionsGroupsList and return the result
            const payload = [...optionsGroupsList, ...filteredUsersList]
            commit('setRecipients', payload)
        },
        /**
         * sending Message
         * @param commit
         * @param payload
         */
        async messageSend({commit}, payload) {
            try {
                commit('setIsSendingMessage', true)
                const res = await sendMessageService(payload)
                commit('setIsSendingMessage', false)
            } catch (error) {
                commit('setIsSendingMessage', false)
                console.error(error);
            }
        },
        /**
         * getting the historic messages
         * @param commit
         * @param payload
         */
        async historicMessages({commit}, payload) {
            try {
                commit("setHistoricMessages", []); // Empty the list before http call to get messages.
                commit("setHistoricMessages", await getHistoricMessages(payload));
            } catch (e) {
                console.error(e);
            }
        },
        /**
         * Récupère les events de communication esign pour une référence donnée
         * et les stocke dans state.emailEvents.
         * @param commit
         * @param payload { reference: string }
         */
        async fetchEmailEvents({commit}, payload: { reference: string }) {
            try {
                // Email du signataire = utilisateur connecté (authModule.user.email), 2e filtre obligatoire de la query
                const email = store.state.authModule?.user?.email
                    || store.getters["authModule/getUserConnectedFromLocalStorage"]?.email
                    || '';
                if (!payload?.reference || !email) {
                    commit("setEmailEvents", []);
                    return;
                }
                // Les signataires d'un même profil sont en « OU » : chacun doit voir le bouton de signature.
                // On charge donc aussi les events des autres acteurs du profil de l'utilisateur ; les siens
                // passent en premier, pour que son propre lien soit préféré.
                const emails = [email, ...sameProfileEmails(payload.reference, email)];
                const results = await Promise.all(
                    emails.map((e) => getEsignClientEventsApi(payload.reference, e).catch(() => null))
                );
                commit("setEmailEvents", results.flatMap((res) => res?.data || []));
            } catch (e) {
                console.error(e);
                commit("setEmailEvents", []);
            }
        },
        /**
         * Récupère les emails (EmailEvent) dont l'utilisateur connecté est expéditeur ou
         * destinataire, tous dossiers confondus, et les stocke dans state.orderUserEmails.
         * Le rattachement à la commande courante est fait côté composant à partir de businessdata.
         * @param commit
         */
        async fetchOrderUserEmails({commit}) {
            try {
                const email = store.state.authModule?.user?.email
                    || store.getters["authModule/getUserConnectedFromLocalStorage"]?.email
                    || '';
                if (!email) {
                    commit("setOrderUserEmails", []);
                    return;
                }
                const res = await getOrderUserEmailsApi(email);
                commit("setOrderUserEmails", res?.data || []);
            } catch (e) {
                console.error(e);
                commit("setOrderUserEmails", []);
            }
        }
    },
    getters: {
        /**
         * this getter retrieves sending status  from state
         * @param state
         */
        getSendingStatus(state) {
            return state.isSendingMessage
        },

        /**
         * this getter retrieves subjects list from state
         * @param state
         */
        getSubjects(state) {
            return state.subjects;
        },
        /**
         * this getter retrieves users list from state
         * @param state
         */
        getUsers(state) {
            return state.users;
        },
        /**
         * this getter retrieves groups list from state
         * @param state
         */
        getGroups(state) {
            return state.groups;
        },

        /**
         * this getter retrieves destinations list from state
         * @param state
         */
        getDestinations: function (state) {
            return state.destinations;
        },
        /**
         * this getter retrieves recipients list from state
         * @param state
         */
        getRecipients(state) {
            return state.recipients;
        },
        /**
         * retrieves the historic messages
         * @param state
         */
        getHistoricMessages(state) {
            return state.historicMessages;
        },
        /**
         * retrieves the esign communication events (emailEvents)
         * @param state
         */
        getEmailEvents(state) {
            return state.emailEvents;
        },
        /**
         * retrieves the emails where the connected user is sender or recipient
         * @param state
         */
        getOrderUserEmails(state) {
            return state.orderUserEmails;
        }
    },
};

export default messagingModule;
